import { GlobalConfirmAlert } from '@/components/ConfirmAlert/ConfirmAlert';
import { Favicon } from '@/Favicon';
import { useIntercom } from '@/hooks/useIntercom';
import { RouterErrorBoundary } from '@/pages/404/ErrorBoundaryPage';
import ErrorPage from '@/pages/404/ErrorPage';
import { APP_LANGUAGES, type GlobalStore } from '@/stores/global.store';
import { isChunkLoadError } from '@/utils/error-logging';
import { useLingui } from '@lingui/react/macro';
import { ErrorBoundary } from '@sentry/react';
import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { z } from 'zod';

export interface RouteContext {
	queryClient: QueryClient;
	globalStore: GlobalStore;
}

const rootSearchSchema = z.object({
	lang: z.enum(APP_LANGUAGES).optional(),
	// Portal deep-link session bootstrap; consumed and stripped by CustomerAuthGuard.
	__s: z.string().optional(),
});

export const Route = createRootRouteWithContext<RouteContext>()({
	validateSearch: rootSearchSchema,
	component: RootLayout,
	errorComponent: ({ error, reset, info }) => <RouterErrorBoundary error={error} reset={reset} info={info} />,
	notFoundComponent: () => <NotFoundPage />,
});

function NotFoundPage() {
	const { t } = useLingui();
	return <ErrorPage title={t`Page Doesn't Exist`} />;
}

function RootLayout() {
	useIntercom();

	return (
		<ErrorBoundary
			fallback={({ error, resetError }) => {
				// Chunk load errors from stale deploys are handled by the vite:preloadError listener
				if (error instanceof Error && isChunkLoadError(error)) {
					return <></>;
				}
				return <RouterErrorBoundary error={error} reset={resetError} />;
			}}
		>
			<Favicon />
			<GlobalConfirmAlert />
			<Outlet />
		</ErrorBoundary>
	);
}
